import { useEffect, useState, useRef } from "react";
import { api, API, formatPrice } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X, Package, Upload } from "lucide-react";
import ImportButton from "@/components/ImportButton";

export default function Products() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ name: "", sku: "", barcode: "", hs_code: "", description: "", unit: "adet", price: "", currency: "TRY", tax_rate: 20, stock: 0, image_url: "" });
  const fileRef = useRef();

  const load = async () => setItems((await api.get("/products")).data);
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, image_url: `${API}${data.url}` }));
      toast.success("Görsel yüklendi");
    } catch { toast.error("Yükleme başarısız"); }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/products", { ...form, price: Number(form.price), stock: Number(form.stock), tax_rate: Number(form.tax_rate) });
      toast.success("Ürün eklendi");
      setOpen(false);
      setForm({ name: "", sku: "", barcode: "", hs_code: "", description: "", unit: "adet", price: "", currency: "TRY", tax_rate: 20, stock: 0, image_url: "" });
      load();
    } catch { toast.error("Kayıt başarısız"); }
  };
  const del = async (id) => { if (!window.confirm("Silinsin mi?")) return; await api.delete(`/products/${id}`); toast.success("Silindi"); load(); };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 dark:text-slate-400 font-bold">Katalog</div>
          <h1 className="font-display text-4xl font-extrabold text-slate-900 dark:text-slate-100 mt-1">Ürünler</h1>
        </div>
        <div className="flex gap-2">
          <ImportButton kind="products" label="Excel İçe Aktar" onDone={load} />
          <button data-testid="add-product" onClick={() => setOpen(true)} className="flex items-center gap-2 px-5 py-3 rounded-xl bg-[#1E2A9C] text-white font-bold hover:bg-[#121A68] shadow-lg shadow-[#1E2A9C]/20"><Plus className="w-4 h-4" /> Yeni Ürün</button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {items.length === 0 && <div className="col-span-full text-center py-16 text-slate-400">Henüz ürün yok</div>}
        {items.map(p => (
          <div key={p.id} className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg hover:shadow-slate-200 hover:-translate-y-0.5 transition-all">
            <div className="aspect-square bg-slate-100 relative overflow-hidden">
              {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" /> : <div className="w-full h-full grid place-items-center"><Package className="w-12 h-12 text-slate-300" /></div>}
              <button data-testid={`del-prod-${p.id}`} onClick={() => del(p.id)} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition p-1.5 bg-white/90 rounded-full text-rose-600 hover:bg-rose-600 hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{p.sku || "-"}</div>
              <div className="font-display font-bold text-slate-900 mt-0.5 line-clamp-2">{p.name}</div>
              {p.hs_code && <div className="text-[10px] text-slate-400 font-mono-num mt-1">HS: {p.hs_code}</div>}
              <div className="mt-3 flex items-center justify-between">
                <div className="font-mono-num text-lg font-bold text-[#1E2A9C]">{formatPrice(p.price, p.currency)}</div>
                <div className="text-xs text-slate-500 font-mono-num">{p.stock} {p.unit}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 grid place-items-center p-4 overflow-y-auto">
          <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-2xl p-8 relative my-8">
            <button type="button" onClick={() => setOpen(false)} className="absolute top-4 right-4"><X className="w-5 h-5" /></button>
            <div className="font-display text-2xl font-extrabold mb-6">Yeni Ürün</div>

            <div className="mb-6">
              <label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Ürün Görseli</label>
              <div className="mt-2 flex items-center gap-4">
                <div className="w-28 h-28 bg-slate-100 rounded-xl overflow-hidden grid place-items-center">
                  {form.image_url ? <img src={form.image_url} alt="" className="w-full h-full object-cover" /> : <Package className="w-8 h-8 text-slate-300" />}
                </div>
                <div>
                  <button type="button" data-testid="upload-product-image" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 text-sm font-semibold"><Upload className="w-4 h-4" /> {uploading ? "Yükleniyor..." : "Görsel Yükle"}</button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
                  <p className="text-xs text-slate-400 mt-2">PNG, JPG. Maks 5MB.</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Ürün Adı</label>
                <input required data-testid="prod-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">SKU / Kod</label>
                <input data-testid="prod-sku" value={form.sku} onChange={(e) => setForm({...form, sku: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg font-mono-num" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Barkod</label>
                <input data-testid="prod-barcode" value={form.barcode} onChange={(e) => setForm({...form, barcode: e.target.value})} placeholder="8690000000000" className="w-full mt-1 px-3 py-2 border rounded-lg font-mono-num" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">HS / GTİP Kodu</label>
                <input data-testid="prod-hs" value={form.hs_code} onChange={(e) => setForm({...form, hs_code: e.target.value})} placeholder="8471.30.00" className="w-full mt-1 px-3 py-2 border rounded-lg font-mono-num" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Birim</label>
                <input data-testid="prod-unit" value={form.unit} onChange={(e) => setForm({...form, unit: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Fiyat</label>
                <input required type="number" step="0.0000001" min="0" data-testid="prod-price" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg font-mono-num" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Para Birimi</label>
                <select data-testid="prod-currency" value={form.currency} onChange={(e) => setForm({...form, currency: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                  {["TRY","USD","EUR"].map(c => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">KDV %</label>
                <input type="number" data-testid="prod-tax" value={form.tax_rate} onChange={(e) => setForm({...form, tax_rate: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg font-mono-num" /></div>
              <div><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Stok</label>
                <input type="number" data-testid="prod-stock" value={form.stock} onChange={(e) => setForm({...form, stock: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg font-mono-num" /></div>
              <div className="col-span-2"><label className="text-xs uppercase font-bold text-slate-500 tracking-widest">Açıklama</label>
                <textarea data-testid="prod-desc" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows="2" className="w-full mt-1 px-3 py-2 border rounded-lg"></textarea></div>
            </div>
            <button data-testid="prod-submit" className="w-full mt-6 py-3 bg-[#1E2A9C] text-white rounded-lg font-bold">Kaydet</button>
          </form>
        </div>
      )}
    </div>
  );
}
